import { useTranslation } from '@mypoz/i18n';
import { Card } from '@mypoz/ui';

import { PageTitle } from '@/components/ui.js';
// The context module, not the account feature's index — see WalletProvider for why.
import { useAccountProfile } from '@/features/account/context.js';
import { WarehouseSection } from '@/features/warehouses/components/WarehouseSection.js';
import { useWarehouses } from '@/features/warehouses/context.js';
import { buttonClass } from '@/lib/styles.js';

/**
 * Three grey cards while the vendor answers.
 *
 * Three because that is roughly what fits above the fold, and shaped like the sections they
 * stand in for so the page does not jump when they are replaced. No spinner: this list arrives
 * in one response, so there is nothing to report progress about.
 */
function LoadingSections() {
  return (
    <div aria-hidden="true" className="flex animate-pulse flex-col gap-5">
      {[0, 1, 2].map((index) => (
        <Card key={index} className="p-0">
          <div className="border-b border-line px-5 py-4 sm:px-6">
            <div className="h-5 w-44 rounded bg-surface-alt" />
          </div>
          <div className="flex flex-col gap-3 px-5 py-4 sm:px-6">
            <div className="h-4 w-full rounded bg-surface-alt" />
            <div className="h-4 w-2/3 rounded bg-surface-alt" />
            <div className="h-4 w-1/2 rounded bg-surface-alt" />
          </div>
        </Card>
      ))}
    </div>
  );
}

/**
 * 仓库地址 — every warehouse a customer may forward purchases to, one stacked section each.
 *
 * Stacked rather than a card grid: the rows are long, wrap differently in six scripts, and are
 * read one at a time while filling in a checkout form on another screen. Columns would shorten
 * the page and make every row harder to use.
 *
 * Live vendor data since MYP-30. A failure here is shown and retryable rather than silently
 * falling back to anything: the one thing this screen must never do is display an address a
 * customer could post a parcel to that is not the warehouse's own.
 */
export function Warehouses() {
  const { t } = useTranslation();
  const { warehouses, status, reload } = useWarehouses();
  const { profile } = useAccountProfile();

  return (
    <>
      <PageTitle title={t('warehouses.title')} subtitle={t('warehouses.subtitle')} />

      {/*
        The single most important sentence on the screen: a parcel whose receiver name carries no
        customer code cannot be matched to an account. Stated once here, and stamped into the
        address and name rows of every section below.
      */}
      <p className="mb-5 text-sm text-muted">
        {t('warehouses.intro')}{' '}
        <span className="rounded bg-brand-soft px-1.5 py-0.5 text-xs font-semibold text-brand">
          {profile.customerId}
        </span>
      </p>

      {status === 'loading' ? <LoadingSections /> : null}

      {status === 'error' ? (
        <Card>
          <h2 className="text-base font-semibold text-ink">{t('warehouses.error.title')}</h2>
          <p className="mt-2 text-sm text-muted">{t('warehouses.error.body')}</p>
          <button type="button" onClick={reload} className={buttonClass('secondary', 'mt-4')}>
            {t('actions.retry')}
          </button>
        </Card>
      ) : null}

      {status === 'ready' && warehouses.length === 0 ? (
        <Card>
          <p className="text-sm text-muted">{t('warehouses.empty')}</p>
        </Card>
      ) : null}

      <div className="flex flex-col gap-5">
        {warehouses.map((warehouse) => (
          <WarehouseSection
            key={warehouse.id}
            address={warehouse}
            customerCode={profile.customerId}
          />
        ))}
      </div>

      {/*
        Announced rather than only drawn, so a screen reader is told the list arrived instead of
        being left on a page whose skeletons it never saw change.
      */}
      <span aria-live="polite" className="sr-only">
        {status === 'ready' ? t('warehouses.loaded', { total: warehouses.length }) : ''}
      </span>
    </>
  );
}
